import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, ArrowLeft } from 'lucide-react';
import { projectAPI } from '../api';
import { locationLabel } from '../components/LocationPicker';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);

export const ProjectViewPage = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectAPI.resolve(id).then((r) => setP(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Project not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex h-96 items-center justify-center"><div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" /></div>;
  if (!p) return <div className="clay-card p-6" data-testid="project-not-found">Project not found.</div>;
  const f = p.financials || {};

  return (
    <div className="space-y-5" data-testid="project-view-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-outfit font-semibold">
            <Building2 className="h-6 w-6 text-amber-600" />
            {p.project_name || 'Project'}
          </h1>
          <p className="mt-1 font-mono text-sm font-semibold" data-testid="pv-serial">{p.project_serial || '\u2014'}</p>
          {p.legacy && <span className="mt-1 inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300" data-testid="pv-legacy-badge">Legacy project record</span>}
        </div>
        {p.lead_id && (
          <Link to={`/leads/${p.lead_id}?tab=project`} data-testid="pv-source-lead-link"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
            <ArrowLeft className="h-4 w-4" /> Source Lead
          </Link>
        )}
      </div>

      <div className="clay-card grid grid-cols-2 gap-4 p-5 text-sm md:grid-cols-4" data-testid="pv-summary">
        <div><div className="text-xs text-muted-foreground">Client</div><div data-testid="pv-client">{p.client_name || '\u2014'}{p.client_type ? ` (${p.client_type.toUpperCase()})` : ''}</div></div>
        <div><div className="text-xs text-muted-foreground">Service</div><div data-testid="pv-service">{p.service || (p.services_required || []).join(', ') || '\u2014'}</div></div>
        <div><div className="text-xs text-muted-foreground">Status</div><div data-testid="pv-status">{p.project_status || '\u2014'}</div></div>
        <div><div className="text-xs text-muted-foreground">Location</div><div data-testid="pv-city">{locationLabel(p) || p.city || '\u2014'}</div></div>
      </div>

      <div className="clay-card p-5" data-testid="pv-finance">
        <h2 className="mb-3 text-base font-medium">Contract Ledger (this project only)</h2>
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          <div><div className="text-xs text-muted-foreground">Current Contract Value</div><div className="font-semibold" data-testid="pv-contract">{money(f.current_contract_value)}</div></div>
          <div><div className="text-xs text-muted-foreground">Confirmed Received</div><div className="text-green-600" data-testid="pv-received">{money(f.total_received)}</div></div>
          <div><div className="text-xs text-muted-foreground">Pending Balance</div><div className="text-orange-600" data-testid="pv-pending">{money(f.pending_balance)}</div></div>
          <div><div className="text-xs text-muted-foreground">Approved Additional Scope</div><div data-testid="pv-scope">{money(f.approved_additional_scope)}</div></div>
        </div>
      </div>

      {p.requirement_notes && (
        <div className="clay-card p-5" data-testid="pv-requirement">
          <h2 className="mb-2 text-base font-medium">Requirement</h2>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{p.requirement_notes}</p>
        </div>
      )}

      {p.assigned_architect_id && (
        <div className="clay-card flex items-center justify-between p-5" data-testid="pv-architect">
          <div><div className="text-xs text-muted-foreground">Architect</div><div className="text-sm">Assigned</div></div>
          <Link to={`/architect/projects/${p.project_id}`} data-testid="pv-architect-workspace-link"
            className="rounded-lg bg-primary px-4 py-2 text-sm text-white">Open Architect Workspace</Link>
        </div>
      )}
    </div>
  );
};

export default ProjectViewPage;
